import React from 'react';
import { Check } from 'lucide-react';

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  description?: string;
  error?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, disabled, className = '', id, checked, ...props }, ref) => {
    const checkboxId = id || (typeof label === 'string' ? `checkbox-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    return (
      <label className={`flex items-start gap-3 select-none cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <div className="relative flex items-center pt-0.5">
          <input
            type="checkbox"
            id={checkboxId}
            ref={ref}
            checked={checked}
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={`w-4 h-4 rounded-md border flex items-center justify-center transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-[#1fb4ac]/40 ${
              checked
                ? 'bg-[#1fb4ac] border-[#1fb4ac] text-white'
                : 'bg-white border-[#CBD5E1] peer-hover:border-[#1fb4ac]'
            }`}
          >
            {checked && <Check className="w-3 h-3 stroke-[3]" />}
          </div>
        </div>
        {(label || description) && (
          <div className="flex-1 text-xs">
            {label && <span className="font-semibold text-[#111111]">{label}</span>}
            {description && <p className="text-[#5F6368] mt-0.5">{description}</p>}
            {error && <p className="text-[#D94A5C] font-medium mt-0.5">{error}</p>}
          </div>
        )}
      </label>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export interface RadioProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  description?: string;
}

export const Radio = React.forwardRef<HTMLInputElement, RadioProps>(
  ({ label, description, disabled, className = '', id, checked, ...props }, ref) => {
    const radioId = id || (typeof label === 'string' ? `radio-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    return (
      <label className={`flex items-start gap-3 select-none cursor-pointer ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
        <div className="relative flex items-center pt-0.5">
          <input
            type="radio"
            id={radioId}
            ref={ref}
            checked={checked}
            disabled={disabled}
            className="peer sr-only"
            {...props}
          />
          <div
            className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all duration-150 peer-focus-visible:ring-2 peer-focus-visible:ring-[#1fb4ac]/40 ${
              checked
                ? 'border-[#1fb4ac] bg-white'
                : 'bg-white border-[#CBD5E1] peer-hover:border-[#1fb4ac]'
            }`}
          >
            {checked && <div className="w-2 h-2 rounded-full bg-[#1fb4ac]" />}
          </div>
        </div>
        {(label || description) && (
          <div className="flex-1 text-xs">
            {label && <span className="font-semibold text-[#111111]">{label}</span>}
            {description && <p className="text-[#5F6368] mt-0.5">{description}</p>}
          </div>
        )}
      </label>
    );
  }
);

Radio.displayName = 'Radio';
